import { useEffect, useRef, useState } from 'react'
import { X, Search, MapPin, Loader2, AlertTriangle, Save } from 'lucide-react'
import { loadGoogleMaps, buildMapUrl } from '../lib/gmaps.js'

/**
 * Google Maps location picker.
 *
 *  Props:
 *    initial?:  { lat, lng, address }          seed marker if any
 *    onClose():                                close without saving
 *    onSave({ lat, lng, address, map_url }):   commit the picked location
 *
 *  Behaviour:
 *    - Default view: SATELLITE, zoom 6 over Thailand if no initial point.
 *    - Search box uses Places Autocomplete; selecting a result drops/moves
 *      the marker and recenters.
 *    - Marker is draggable; on dragend we reverse-geocode for the address.
 *    - Save emits the canonical https://www.google.com/maps?q=lat,lng link.
 *
 *  Requires VITE_GOOGLE_MAPS_API_KEY in the build env.
 */
export default function MapPickerModal({ initial, onClose, onSave }) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  const mapRef     = useRef(null)
  const inputRef   = useRef(null)
  const mapObjRef  = useRef(null)
  const markerRef  = useRef(null)
  const geocoderRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [lat, setLat] = useState(initial?.lat ?? null)
  const [lng, setLng] = useState(initial?.lng ?? null)
  const [address, setAddress] = useState(initial?.address || '')

  useEffect(() => {
    if (!apiKey) {
      setError('Missing VITE_GOOGLE_MAPS_API_KEY — add it to .env then rebuild.')
      setLoading(false)
      return
    }
    let cancelled = false
    loadGoogleMaps(apiKey).then((google) => {
      if (cancelled || !mapRef.current) return
      const start = (initial?.lat != null && initial?.lng != null)
        ? { lat: initial.lat, lng: initial.lng }
        : { lat: 13.7563, lng: 100.5018 }   // Bangkok center
      const map = new google.maps.Map(mapRef.current, {
        center:     start,
        zoom:       (initial?.lat != null) ? 17 : 6,
        mapTypeId:  google.maps.MapTypeId.HYBRID,    // satellite + labels
        streetViewControl: false,
        fullscreenControl: false,
      })
      mapObjRef.current  = map
      geocoderRef.current = new google.maps.Geocoder()

      const marker = new google.maps.Marker({
        map,
        position:  start,
        draggable: true,
        visible:   initial?.lat != null,
      })
      markerRef.current = marker
      marker.addListener('dragend', () => {
        const p = marker.getPosition()
        if (!p) return
        setLat(p.lat()); setLng(p.lng())
        reverseGeocode(p.lat(), p.lng())
      })

      map.addListener('click', (e) => {
        if (!e.latLng) return
        const lat = e.latLng.lat(), lng = e.latLng.lng()
        marker.setPosition(e.latLng); marker.setVisible(true)
        setLat(lat); setLng(lng)
        reverseGeocode(lat, lng)
      })

      // Places Autocomplete on the search input
      if (inputRef.current && google.maps.places) {
        const ac = new google.maps.places.Autocomplete(inputRef.current, {
          fields: ['geometry', 'formatted_address', 'name'],
          componentRestrictions: { country: 'th' },
        })
        ac.addListener('place_changed', () => {
          const place = ac.getPlace()
          if (!place?.geometry?.location) return
          const p = place.geometry.location
          const lat = p.lat(), lng = p.lng()
          map.setCenter(p); map.setZoom(17)
          marker.setPosition(p); marker.setVisible(true)
          setLat(lat); setLng(lng)
          setAddress(place.formatted_address || place.name || '')
        })
      }

      setLoading(false)
    }).catch((e) => {
      if (cancelled) return
      setError(e?.message || 'Failed to load Google Maps')
      setLoading(false)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function reverseGeocode(lat, lng) {
    const g = geocoderRef.current
    if (!g) return
    g.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        setAddress(results[0].formatted_address || '')
      }
    })
  }

  function commit() {
    if (lat == null || lng == null) {
      setError('Drop a pin or search a location first.')
      return
    }
    onSave({ lat, lng, address: address || null, map_url: buildMapUrl(lat, lng) })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-zinc-900/40 backdrop-blur-[1px]" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[820px] max-h-[92vh] flex flex-col">
        <header className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-700/80 font-semibold">Location</div>
            <h2 className="text-[16px] font-bold text-zinc-900">เลือกตำแหน่งบนแผนที่</h2>
          </div>
          <div className="flex-1 max-w-[420px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              placeholder="ค้นหาสถานที่…  (e.g. โรงพยาบาลรามาธิบดี)"
              className="w-full pl-8 pr-3 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none bg-white text-[13px]"
            />
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 inline-flex items-center justify-center shrink-0">
            <X size={16} />
          </button>
        </header>

        <div className="relative flex-1 min-h-[420px]">
          <div ref={mapRef} className="absolute inset-0 bg-zinc-100" />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-zinc-500 text-[13px]">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading map…
            </div>
          )}
          {error && (
            <div className="absolute top-3 left-3 right-3 bg-rose-50 ring-1 ring-rose-200 text-rose-700 px-3 py-2 rounded-lg text-[12.5px] inline-flex items-center gap-1.5">
              <AlertTriangle size={13} /> {error}
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
          <div className="text-[12px] text-zinc-500 min-w-0 flex items-center gap-1.5">
            <MapPin size={13} className="text-amber-500 shrink-0" />
            {lat != null && lng != null ? (
              <span className="truncate">
                <span className="tabular-nums text-zinc-700">{lat.toFixed(5)}, {lng.toFixed(5)}</span>
                {address ? <span className="text-zinc-400"> · {address}</span> : null}
              </span>
            ) : (
              <span className="text-zinc-400">คลิกหรือลากเพื่อปักหมุด</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onClose} className="px-3 py-1.5 rounded-md text-[13px] text-zinc-600 hover:bg-zinc-100">ยกเลิก</button>
            <button onClick={commit} disabled={lat == null || lng == null}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-medium disabled:opacity-50">
              <Save size={14} /> บันทึก
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
